import { Navigate, createBrowserRouter, createMemoryRouter } from 'react-router-dom';
import type { RouteObject } from 'react-router-dom';
import type { ComponentType } from 'react';
import { AppShell } from '@app/layouts/AppShell';
import { AuthenticatedGuard, PermissionGuard, PublicOnlyGuard } from '@app/routing/guards';
import { LegacyWhatsAppRedirect } from '@app/routing/LegacyWhatsAppRedirect';
import {
  AuthLayout,
  ForgotPasswordPage,
  LoginPage,
  ResetPasswordPage,
  VerifyOtpPage,
} from '@features/auth/pages/AuthPages';
import type { Permission } from '@shared/auth/permissions';
import {
  AccessDeniedPage,
  GlobalErrorPage,
  NotFoundPage,
  SessionExpiredPage,
} from '@shared/components/SystemStates';

function protectedRoute(
  path: string,
  loader: () => Promise<{ Component: ComponentType }>,
  permission?: Permission,
): RouteObject {
  const page: RouteObject = { path, lazy: loader };
  return permission === undefined
    ? page
    : { element: <PermissionGuard required={permission} />, children: [page] };
}

export const appRoutes: RouteObject[] = [
  {
    element: <PublicOnlyGuard />,
    children: [
      {
        element: <AuthLayout />,
        children: [
          { path: '/login', element: <LoginPage /> },
          { path: '/verify-otp', element: <VerifyOtpPage /> },
          { path: '/forgot-password', element: <ForgotPasswordPage /> },
          { path: '/reset-password', element: <ResetPasswordPage /> },
        ],
      },
    ],
  },
  { path: '/session-expired', element: <SessionExpiredPage /> },
  { path: '/access-denied', element: <AccessDeniedPage /> },
  {
    element: <AuthenticatedGuard />,
    errorElement: <GlobalErrorPage />,
    children: [
      protectedRoute(
        '/knowledge-base/articles/:articleId/preview',
        async () => ({
          Component: (await import('@features/knowledge/KnowledgePage')).KnowledgePreviewPage,
        }),
        'knowledge.view',
      ),
      {
        element: <AppShell />,
        children: [
          { index: true, element: <Navigate to="/dashboard" replace /> },
          protectedRoute('/notifications', async () => ({
            Component: (await import('@features/notifications/NotificationsPage')).default,
          })),
          protectedRoute('/notification-preferences', async () => ({
            Component: (await import('@features/notifications/NotificationPreferencesPage'))
              .default,
          })),
          protectedRoute(
            '/notification-settings',
            async () => ({
              Component: (await import('@features/notifications/NotificationSettingsPage')).default,
            }),
            'notifications.settings.view',
          ),
          protectedRoute('/search', async () => ({
            Component: (await import('@app/layouts/GlobalSearchResultsPage'))
              .GlobalSearchResultsPage,
          })),
          protectedRoute('/assigned-to-me', async () => ({
            Component: (await import('@features/assigned-work/AssignedWorkPage')).default,
          })),
          protectedRoute(
            '/customers-feedback/ea-customer-survey',
            async () => ({
              Component: (await import('@features/feedback/EaCustomerSurveyPage')).default,
            }),
            'feedback.ea_survey.view',
          ),
          protectedRoute(
            '/customers-feedback/live-chat',
            async () => ({
              Component: (await import('@features/feedback/FeedbackPage')).LiveChatFeedbackPage,
            }),
            'feedback.livechat.view',
          ),
          protectedRoute(
            '/customers-feedback/recovery-tracking',
            async () => ({
              Component: (await import('@features/feedback/FeedbackPage'))
                .RecoveryTrackingFeedbackPage,
            }),
            'feedback.recovery.view',
          ),
          protectedRoute(
            '/dashboard',
            async () => ({
              Component: (await import('@features/dashboard/DashboardPage')).default,
            }),
            'dashboard.view',
          ),
          ...[
            '/inbox',
            '/inbox/:conversationId',
            '/inbox/live-chat',
            '/inbox/live-chat/:conversationId',
            '/inbox/email',
          ].map((path) =>
            protectedRoute(
              path,
              async () => ({ Component: (await import('@features/inbox/InboxPage')).default }),
              path.startsWith('/inbox/live-chat') ? 'livechat.view' : 'inbox.view',
            ),
          ),
          {
            element: <PermissionGuard required="knowledge.connectors.view" />,
            children: [
              {
                path: '/knowledge-base/connectors',
                element: <Navigate to="/connectors/rest-source" replace />,
              },
            ],
          },
          protectedRoute(
            '/inbox/whatsapp',
            async () => ({ Component: (await import('@features/inbox/InboxPage')).default }),
            'whatsapp.view',
          ),
          protectedRoute(
            '/inbox/whatsapp/:clientId',
            async () => ({ Component: (await import('@features/inbox/InboxPage')).default }),
            'whatsapp.view',
          ),
          protectedRoute(
            '/tickets',
            async () => ({
              Component: (await import('@features/operations/TicketsPage')).TicketsPage,
            }),
            'tickets.view',
          ),
          protectedRoute(
            '/tickets/:ticketId',
            async () => ({
              Component: (await import('@features/operations/TicketsPage')).TicketDetailsPage,
            }),
            'tickets.view',
          ),
          ...['/calls', '/calls/:callId'].map((path) =>
            protectedRoute(
              path,
              async () => ({
                Component: (await import('@features/operations/CallEnquiriesPage'))
                  .CallEnquiriesPage,
              }),
              'calls.view',
            ),
          ),
          ...[
            '/knowledge-base',
            '/knowledge-base/articles/new',
            '/knowledge-base/articles/:articleId',
            '/knowledge-base/articles/:articleId/edit',
            '/knowledge-base/articles/:articleId/versions',
            '/knowledge-base/articles/:articleId/versions/:version',
            '/knowledge-base/articles/:articleId/versions/:version/compare',
            '/knowledge-base/approvals',
            '/knowledge-base/categories',
            '/knowledge-base/tags',
            '/knowledge-base/audience',
            '/knowledge-base/languages',
            '/knowledge-base/feedback',
            '/knowledge-base/analytics',
            '/knowledge-base/settings',
          ].map((path) =>
            protectedRoute(
              path,
              async () => ({
                Component: (await import('@features/knowledge/KnowledgePage')).default,
              }),
              'knowledge.view',
            ),
          ),
          protectedRoute(
            '/knowledge-base/gaps',
            async () => ({
              Component: (await import('@features/knowledge/KnowledgePage')).default,
            }),
            'knowledge.gaps.view',
          ),
          protectedRoute(
            '/escalations',
            async () => ({
              Component: (await import('@features/operations/EscalationsPage')).default,
            }),
            'escalations.view',
          ),
          protectedRoute(
            '/reports',
            async () => ({
              Component: (await import('@features/reports/ReportsPage')).ReportsPage,
            }),
            'reports.view',
          ),
          protectedRoute(
            '/calls/cupola',
            async () => ({
              Component: (await import('@features/calls/CupolaCallsPage')).CupolaCallsPage,
            }),
            'calls.settings.view',
          ),
          {
            element: <PermissionGuard required="reports.view" />,
            children: [
              protectedRoute(
                '/live-chat/dashboard',
                async () => ({
                  Component: (await import('@features/reports/LiveChatDashboardPage'))
                    .LiveChatDashboardPage,
                }),
                'livechat.view',
              ),
            ],
          },
          ...(['users', 'roles', 'teams', 'entities', 'channels'] as const).map((resource) =>
            protectedRoute(
              `/${resource}`,
              async () => {
                const { AdministrationPage } =
                  await import('@features/administration/AdministrationPages');
                return { Component: () => <AdministrationPage resource={resource} /> };
              },
              `${resource}.view` as Permission,
            ),
          ),
          protectedRoute(
            '/sla',
            async () => ({
              Component: (await import('@features/administration/AdministrationPages')).SlaPage,
            }),
            'sla.view',
          ),
          protectedRoute(
            '/settings',
            async () => ({
              Component: (await import('@features/administration/AdministrationPages'))
                .SettingsPage,
            }),
            'settings.view',
          ),
          protectedRoute(
            '/helix-inbox',
            async () => ({
              Component: (await import('@features/helix-inbox/HelixInboxPage')).default,
            }),
            'mailbox.inbox.view',
          ),
          protectedRoute(
            '/connectors/kb-mcp',
            async () => ({
              Component: (await import('@features/knowledge/KnowledgeMcpConnectorsPage')).default,
            }),
            'knowledge.connectors.view',
          ),
          protectedRoute(
            '/connectors/rest-source',
            async () => ({
              Component: (await import('@features/knowledge/KnowledgeConnectorsPage')).default,
            }),
            'knowledge.connectors.view',
          ),
          protectedRoute(
            '/connectors/rest-delivery',
            async () => ({
              Component: (await import('@features/knowledge/KnowledgeRestConnectorsPage')).default,
            }),
            'knowledge.connectors.view',
          ),
          protectedRoute(
            '/settings/kb-mcp',
            async () => ({
              Component: (await import('@features/knowledge/KnowledgeMcpConnectorsPage')).default,
            }),
            'knowledge.connectors.view',
          ),
          protectedRoute(
            '/settings/kb-delivery',
            async () => ({
              Component: (await import('@features/knowledge/KnowledgeRestConnectorsPage')).default,
            }),
            'knowledge.connectors.view',
          ),
          protectedRoute(
            '/live-chat/mappings',
            async () => ({
              Component: (await import('@features/administration/AdministrationPages'))
                .LiveChatMappingsPage,
            }),
            'livechat.administrator',
          ),
          protectedRoute(
            '/settings/live-chat-mappings',
            () =>
              Promise.resolve({
                Component: () => <LegacyWhatsAppRedirect to="/live-chat/mappings" />,
              }),
            'livechat.administrator',
          ),
          protectedRoute(
            '/whatsapp/agent-mappings',
            async () => ({
              Component: (await import('@features/administration/AdministrationPages'))
                .WhatsAppMappingsPage,
            }),
            'whatsapp.administrator',
          ),
          protectedRoute(
            '/whatsapp/audit',
            async () => ({
              Component: (await import('@features/administration/WhatsAppAuditPage'))
                .WhatsAppAuditPage,
            }),
            'whatsapp.audit.view',
          ),
          ...(
            [
              ['/whatsapp/dashboard', 'WhatsAppAdminDashboardPage', 'whatsapp.settings.view'],
              ['/whatsapp/agents', 'WhatsAppAdminAgentsPage', 'whatsapp.settings.view'],
              ['/whatsapp/clients', 'WhatsAppAdminClientsPage', 'whatsapp.settings.view'],
              ['/whatsapp/profiles', 'WhatsAppAdminProfilesPage', 'whatsapp.settings.view'],
              ['/whatsapp/action-reasons', 'WhatsAppAdminReasonsPage', 'whatsapp.settings.view'],
              [
                '/whatsapp/message-shortcuts',
                'WhatsAppAdminMessageShortcutsPage',
                'whatsapp.settings.view',
              ],
            ] as Array<[string, string, Permission]>
          ).map(([path, component, permission]) =>
            protectedRoute(
              path,
              async () => {
                const pages = await import('@features/administration/WhatsAppAdministrationPages');
                return { Component: pages[component as keyof typeof pages] };
              },
              permission,
            ),
          ),
          ...(
            [
              ...[
                'audit-trail',
                'background-jobs',
                'operation-messages',
                'ersa-start-messages',
                'ai-polish-logs',
              ].map((tab): [string, string] => [`/whatsapp/${tab}`, `/whatsapp/audit?tab=${tab}`]),
              ['/whatsapp', '/whatsapp/dashboard'],
              ['/settings/whatsapp', '/whatsapp/dashboard'],
              ['/settings/whatsapp/dashboard', '/whatsapp/dashboard'],
              ['/settings/whatsapp-mappings', '/whatsapp/agent-mappings'],
              ['/settings/ersa-start-messages', '/whatsapp/ersa-start-messages'],
              ...[
                'agents',
                'clients',
                'profiles',
                'action-reasons',
                'message-shortcuts',
                'audit-trail',
                'background-jobs',
                'operation-messages',
                'ersa-start-messages',
                'ai-polish-logs',
                'agent-mappings',
              ].map((page): [string, string] => [
                `/settings/whatsapp/${page}`,
                `/whatsapp/${page}`,
              ]),
            ] as Array<[string, string]>
          ).map(([path, to]) => ({ path, element: <LegacyWhatsAppRedirect to={to} /> })),
          protectedRoute('/profile', async () => ({
            Component: (await import('@features/administration/AdministrationPages')).ProfilePage,
          })),
        ],
      },
    ],
  },
  { path: '*', element: <NotFoundPage /> },
];

export const createAppRouter = () => createBrowserRouter(appRoutes);
export const createTestRouter = (initialEntries: string[]) =>
  createMemoryRouter(appRoutes, { initialEntries });
